import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Award, Target, Users, Shield } from 'lucide-react';
import { Section } from '../components/ui/Section';
import { Stats } from '../components/ui/Stats';
import { OptimizedImage } from '../components/ui/OptimizedImage';

// Create properly typed motion components
const MotionDiv = motion.div;
const MotionH1 = motion.h1;
const MotionP = motion.p;

export default function About() {
  return (
    <div>
      {/* Hero Section */}
      <div className="relative py-24 bg-primary">        <div className="absolute inset-0 overflow-hidden">          <img
            src="https://images.unsplash.com/photo-1494412651409-8963ce7935a7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
            alt="Container shipping"
            className="w-full h-full object-cover opacity-20"
            onError={(e) => {
              // Fallback to local Photo2 image first, then another Unsplash image
              if (e.currentTarget.src.includes('unsplash')) {
                e.currentTarget.src = "/assets/images/Photo2.jpg";
                console.log("About hero image fallback: trying Photo2");
              } else {
                e.currentTarget.src = "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80";
                console.warn("About hero image failed, using secondary fallback");
              }
            }}
            onLoad={() => {
              console.log("About hero image loaded successfully");
            }}
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center text-white">
            <MotionH1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            >
              About Mega Marine Container Terminals
            </MotionH1>
            <MotionP
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl mb-10"
            >
              Delivering excellence in container yard operations since inception
            </MotionP>
          </div>
        </div>
      </div>
      
      {/* Company Overview */}
      <Section>
        <div className="grid md:grid-cols-2 gap-12 items-center">          <MotionDiv
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-lg overflow-hidden shadow-lg"
          >            <OptimizedImage
              src="https://images.unsplash.com/photo-1616831575954-f74aa42ebd41?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
              alt="Container yard operations"
              fallbackSrc="/assets/images/Photo2.jpg"
              className="w-full h-64 md:h-80 object-cover"
              containerClassName="h-64 md:h-80"
            />
          </MotionDiv>
          
          <MotionDiv
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Our Story</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Mega Marine Container Terminals was established with a vision to transform the container yard 
              operations landscape in India. What began as a modest operation has grown into a trusted name 
              in the shipping and logistics industry, known for its unwavering commitment to transparency and quality.
            </p>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Today, with our strategically located yards in Kandla and Mundra, we offer comprehensive container 
              handling, storage, and repair services to clients across the maritime sector. Our growth has been 
              built on strong relationships, ethical business practices, and a customer-first approach.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              As we continue to evolve, our core values remain unchanged – providing honest, high-quality services 
              that reduce risk and build lasting trust with our partners.
            </p>
          </MotionDiv>
        </div>
      </Section>
      
      {/* Mission & Vision */}
      <Section className="bg-gray-50 dark:bg-navy">
        <div className="grid md:grid-cols-2 gap-12">
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white dark:bg-navy-light p-8 rounded-lg shadow-sm border border-gray-100 dark:border-navy-dark"
          >
            <div className="w-16 h-16 mb-6 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Target className="h-8 w-8" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Our Mission</h3>
            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
              "To deliver honest, high-quality empty yard and container repair solutions by upholding transparency, 
              reducing customer risk, and setting new standards of trust in the shipping industry."
            </p>
          </MotionDiv>
          
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-primary text-white p-8 rounded-lg shadow-sm"
          >
            <div className="w-16 h-16 mb-6 rounded-full bg-white/10 flex items-center justify-center">
              <Building2 className="h-8 w-8" />
            </div>
            <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
            <p className="text-gray-100 text-lg leading-relaxed">
              "To become the most trusted name in container yard operations by redefining industry ethics, 
              promoting fair practices, and creating a zero-risk environment for our partners."
            </p>
          </MotionDiv>
        </div>
      </Section>
      
      {/* Core Values */}
      <Section
        title="Our Core Values"
        subtitle="The principles that guide everything we do at Mega Marine Container Terminals"
      >
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white dark:bg-navy-light p-6 rounded-lg shadow-sm border border-gray-100 dark:border-navy-dark text-center"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Shield className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">Integrity</h3>
            <p className="text-gray-700 dark:text-gray-300">
              We conduct our business with unwavering honesty and ethical standards, ensuring transparency in all operations.
            </p>
          </MotionDiv>
          
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white dark:bg-navy-light p-6 rounded-lg shadow-sm border border-gray-100 dark:border-navy-dark text-center"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Award className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">Excellence</h3>
            <p className="text-gray-700 dark:text-gray-300">
              We strive for the highest quality in our services, continuously improving to exceed industry standards.
            </p>
          </MotionDiv>
          
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white dark:bg-navy-light p-6 rounded-lg shadow-sm border border-gray-100 dark:border-navy-dark text-center"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Users className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">Partnership</h3>
            <p className="text-gray-700 dark:text-gray-300">
              We build lasting relationships with our clients, treating their containers with the same care we would our own.
            </p>
          </MotionDiv>
          
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white dark:bg-navy-light p-6 rounded-lg shadow-sm border border-gray-100 dark:border-navy-dark text-center"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Building2 className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">Innovation</h3>
            <p className="text-gray-700 dark:text-gray-300">
              We embrace new technologies and methodologies to enhance efficiency and deliver better solutions.
            </p>
          </MotionDiv>
        </div>
      </Section>
      
      {/* Stats Section */}
      <Section className="bg-primary text-white">
        <h2 className="text-3xl font-bold mb-12 text-center">Our Impact in Numbers</h2>
        <Stats
          items={[
            { value: "15+", label: "Years Experience" },
            { value: "100+", label: "Regular Clients" },
            { value: "2000+", label: "Containers Handled Monthly" },
            { value: "50+", label: "Dedicated Team Members" },
          ]}
        />
      </Section>
    </div>
  );
}


